import ScienceIcon from '@mui/icons-material/Science'
import SearchIcon from '@mui/icons-material/Search'
import { Alert, Box, InputAdornment, List, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'

import ChatListItem from './ChatListItem'
import ConnectionStatus from './ConnectionStatus'

/**
 * ChatList - Displays a list of conversations with search
 *
 * @param {Array} conversations - Array of conversation objects
 * @param {string} selectedConversationId - ID of the selected conversation
 * @param {function} onSelectConversation - Callback when conversation is selected
 * @param {string} connectionState - WebSocket connection state
 * @param {Object} sx - Additional styles to apply
 */
export default function ChatList({ conversations = [], selectedConversationId, onSelectConversation, connectionState, sx = {} }) {
  const [searchQuery, setSearchQuery] = useState('')

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      conv.title?.toLowerCase().includes(query) || conv.participants?.some((p) => p.name?.toLowerCase().includes(query)) || conv.lastMessage?.text?.toLowerCase().includes(query)
    )
  })

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Messages
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <ConnectionStatus connectionState={connectionState} />
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
            },
          }}
        />
      </Box>

      <List
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 0,
        }}
      >
        {filteredConversations.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </Typography>
          </Box>
        ) : (
          filteredConversations.map((conversation) => (
            <ChatListItem
              key={conversation.id}
              conversation={conversation}
              selected={conversation.id === selectedConversationId}
              onClick={() => onSelectConversation(conversation.id)}
            />
          ))
        )}
      </List>
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Alert
          severity="warning"
          icon={<ScienceIcon />}
          sx={{
            fontSize: '0.75rem',
            '& .MuiAlert-message': {
              fontSize: '0.75rem',
            },
          }}
        >
          Chat is currently in beta testing. You might experience occasional issues, but we're working hard to improve it!
        </Alert>
      </Box>
    </Box>
  )
}
